<!-- 我的订单页 -->
<template>
  <div class="my-orders-page">
    <Navbar />

    <!-- 页头 -->
    <section class="page-hero">
      <div class="page-hero-overlay"></div>
      <div class="container">
        <div class="page-hero-content">
          <span class="section-tag">MY ORDERS</span>
          <h1 class="page-hero-title">我的订单</h1>
          <p class="page-hero-desc">查看您的凹陷修复订单状态</p>
          <div class="hero-divider">
            <span class="line"></span><span class="diamond"></span><span class="line"></span>
          </div>
        </div>
      </div>
    </section>

    <!-- 订单列表 -->
    <section class="orders-section">
      <div class="container">
        <div class="orders-wrapper">
          <!-- 状态筛选 -->
          <div class="filter-bar">
            <button :class="['filter-btn', { active: filterStatus === '' }]" @click="setFilter('')">全部</button>
            <button :class="['filter-btn', { active: filterStatus === 1 }]" @click="setFilter(1)">待接单</button>
            <button :class="['filter-btn', { active: filterStatus === 2 }]" @click="setFilter(2)">维修中</button>
            <button :class="['filter-btn', { active: filterStatus === 3 }]" @click="setFilter(3)">已完成</button>
            <button :class="['filter-btn', { active: filterStatus === 4 }]" @click="setFilter(4)">已取消</button>
            <span class="filter-count">共 {{ total }} 条</span>
            <button class="submit-order-btn" @click="router.push('/order-submit')">+ 提交订单</button>
          </div>

          <!-- 订单卡片列表 -->
          <div v-loading="loading" class="orders-list">
            <el-empty v-if="!loading && orders.length === 0" description="暂无订单" />
            <div v-for="order in orders" :key="order._id" class="order-card" @click="viewDetail(order)">
              <div class="order-card-header">
                <span class="order-no">{{ order.orderNo }}</span>
                <el-tag :type="statusTagType(order.status)" size="small">{{ statusLabel(order.status) }}</el-tag>
              </div>
              <div class="order-card-body">
                <div class="order-info-row">
                  <span class="info-label">客户</span>
                  <span class="info-value">{{ order.customerName }} · {{ order.phone }}</span>
                </div>
                <div class="order-info-row" v-if="order.plateNumber">
                  <span class="info-label">车牌</span>
                  <span class="info-value">{{ order.plateNumber }}</span>
                </div>
                <div class="order-info-row" v-if="order.carModel">
                  <span class="info-label">车型</span>
                  <span class="info-value">{{ order.carModel }}</span>
                </div>
                <div class="order-info-row" v-if="order.damageLocation">
                  <span class="info-label">凹陷位置</span>
                  <span class="info-value">{{ order.damageLocation }}</span>
                </div>
              </div>
              <div class="order-card-footer">
                <span class="order-amount">{{ order.estimatedAmount ? '¥' + order.estimatedAmount : '待报价' }}</span>
                <span class="order-time">{{ formatTime(order.createdAt) }}</span>
              </div>
            </div>
          </div>

          <!-- 分页 -->
          <el-pagination v-if="total > pageSize"
            v-model:current-page="currentPage" :page-size="pageSize"
            :total="total" layout="total, prev, pager, next"
            @current-change="loadOrders"
            class="pagination"
          />
        </div>
      </div>
    </section>

    <!-- 订单详情弹窗 -->
    <el-dialog v-model="detailVisible" title="订单详情" width="600px" :close-on-click-modal="false">
      <el-descriptions :column="2" border v-if="detailOrder">
        <el-descriptions-item label="订单编号">{{ detailOrder.orderNo }}</el-descriptions-item>
        <el-descriptions-item label="订单状态">
          <el-tag :type="statusTagType(detailOrder.status)">{{ statusLabel(detailOrder.status) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="客户姓名">{{ detailOrder.customerName }}</el-descriptions-item>
        <el-descriptions-item label="联系电话">{{ detailOrder.phone }}</el-descriptions-item>
        <el-descriptions-item label="车牌号码">{{ detailOrder.plateNumber || '——' }}</el-descriptions-item>
        <el-descriptions-item label="车型">{{ detailOrder.carModel || '——' }}</el-descriptions-item>
        <el-descriptions-item label="车辆颜色">{{ detailOrder.carColor || '——' }}</el-descriptions-item>
        <el-descriptions-item label="凹陷位置">{{ detailOrder.damageLocation || '——' }}</el-descriptions-item>
        <el-descriptions-item label="金额">{{ detailOrder.estimatedAmount || '——' }} 元</el-descriptions-item>
        <el-descriptions-item label="下单时间">{{ formatTime(detailOrder.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="完成时间">{{ formatTime(detailOrder.completedAt) || '——' }}</el-descriptions-item>
        <el-descriptions-item label="损伤描述" :span="2">{{ detailOrder.damageDesc || '——' }}</el-descriptions-item>
        <el-descriptions-item label="客户备注" :span="2">{{ detailOrder.remark || '——' }}</el-descriptions-item>
      </el-descriptions>

      <!-- 图片 -->
      <div class="image-section" v-if="detailBefore.length">
        <div class="section-title">修复前照片</div>
        <div class="image-grid">
          <el-image v-for="img in detailBefore" :key="img._id"
            :src="imgUrl(img.imageUrl)" :preview-src-list="detailBefore.map(i => imgUrl(i.imageUrl))"
            :initial-index="detailBefore.indexOf(img)" fit="cover" class="thumb-img" />
        </div>
      </div>
      <div class="image-section" v-if="detailAfter.length">
        <div class="section-title">修复后照片</div>
        <div class="image-grid">
          <el-image v-for="img in detailAfter" :key="img._id"
            :src="imgUrl(img.imageUrl)" :preview-src-list="detailAfter.map(i => imgUrl(i.imageUrl))"
            :initial-index="detailAfter.indexOf(img)" fit="cover" class="thumb-img" />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import Navbar from '@/components/Navbar.vue'

const router = useRouter()
const store = useStore()

// 未登录跳转
onMounted(() => {
  if (!store.getters.isLoggedIn) {
    router.push({ path: '/auth', query: { redirect: '/my-orders' } })
    return
  }
  loadOrders()
})

const orders = ref([])
const loading = ref(false)
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)
const filterStatus = ref('')

const detailVisible = ref(false)
const detailOrder = ref(null)
const detailBefore = ref([])
const detailAfter = ref([])

const statusLabel = v => ({ 1: '待接单', 2: '维修中', 3: '已完成', 4: '已取消' }[v] || '——')
const statusTagType = v => ({ 1: 'warning', 2: '', 3: 'success', 4: 'info' }[v] || '')
const imgUrl = p => `${p}`
const formatTime = t => {
  if (!t) return '——'
  const d = new Date(t)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${pad(d.getMonth()+1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const setFilter = (v) => { filterStatus.value = v; currentPage.value = 1; loadOrders() }

const loadOrders = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams({ page: currentPage.value, limit: pageSize.value })
    if (filterStatus.value !== '') params.append('status', filterStatus.value)
    const res = await fetch(`/webapi/dent/order/mylist?${params}`, {
      headers: { 'Authorization': `Bearer ${store.state.token}` }
    })
    const data = await res.json()
    if (data.ActionType === 'OK') {
      orders.value = data.data.list || []
      total.value = data.data.total
    }
  } catch (e) { console.error(e) }
  finally { loading.value = false }
}

const viewDetail = async (order) => {
  try {
    const res = await fetch(`/webapi/dent/order/mydetail/${order._id}`, {
      headers: { 'Authorization': `Bearer ${store.state.token}` }
    })
    const data = await res.json()
    if (data.ActionType === 'OK') {
      detailOrder.value = data.data.orderInfo
      detailBefore.value = data.data.beforeImages || []
      detailAfter.value  = data.data.afterImages  || []
      detailVisible.value = true
    }
  } catch (e) { console.error(e) }
}
</script>

<style lang="scss" scoped>
$gold: #d4af37;
$dark: #1a1a1a;
$gray: #6b7280;

.page-hero {
  position: relative; min-height: 220px; margin-top: 72px;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  display: flex; align-items: center;
}

.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

.page-hero-content { position: relative; z-index: 1; text-align: center; padding: 50px 0; }

.section-tag {
  display: inline-block; font-size: 12px; font-weight: 600; letter-spacing: 4px;
  color: $gold; margin-bottom: 16px;
}

.page-hero-title { font-size: 52px; font-weight: 800; color: #fff; margin-bottom: 16px; letter-spacing: 2px; }
.page-hero-desc  { font-size: 17px; color: rgba(255,255,255,0.6); margin-bottom: 24px; letter-spacing: 1px; }

.hero-divider {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  .line { width: 50px; height: 1px; background: rgba(212,175,55,0.5); }
  .diamond { width: 8px; height: 8px; background: $gold; transform: rotate(45deg); }
}

.orders-section { padding: 40px 0 80px; background: #f5f7fa; }

.orders-wrapper { max-width: 900px; margin: 0 auto; }

.filter-bar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 20px;
  .filter-btn {
    padding: 6px 18px; border: 1px solid #e0e0e0; border-radius: 20px;
    background: #fff; font-size: 13px; color: $gray; cursor: pointer; transition: all 0.2s;
    &.active { background: $gold; color: #fff; border-color: $gold; }
    &:hover:not(.active) { border-color: $gold; color: $gold; }
  }
  .filter-count { margin-left: auto; font-size: 13px; color: #bbb; }
  .submit-order-btn {
    margin-left: 12px;
    padding: 6px 20px;
    background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
    color: #fff;
    border: none;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { background: linear-gradient(135deg, #e6c040 0%, #d4af37 100%); transform: translateY(-1px); }
  }
}

.orders-list {
  display: flex; flex-direction: column; gap: 12px;
}

.order-card {
  background: #fff; border-radius: 12px; padding: 20px 24px;
  cursor: pointer; transition: all 0.3s; border: 1px solid transparent;
  &:hover { border-color: rgba(212,175,55,0.3); box-shadow: 0 4px 16px rgba(212,175,55,0.08); }
}

.order-card-header {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px;
  .order-no { font-size: 14px; font-weight: 600; color: $gold; }
}

.order-card-body { margin-bottom: 14px; }
.order-info-row { display: flex; gap: 8px; margin-bottom: 6px; font-size: 14px;
  .info-label { color: $gray; width: 70px; flex-shrink: 0; }
  .info-value { color: $dark; flex: 1; }
}

.order-card-footer {
  display: flex; align-items: center; justify-content: space-between;
  .order-amount { font-size: 16px; font-weight: 700; color: $dark; }
  .order-time   { font-size: 12px; color: #bbb; }
}

.pagination { margin-top: 24px; display: flex; justify-content: flex-end; }

.image-section { margin-top: 20px; padding-top: 16px; border-top: 1px solid #eee;
  .section-title { font-size: 14px; font-weight: 600; color: #333; margin-bottom: 12px; }
  .image-grid { display: flex; flex-wrap: wrap; gap: 8px; }
  .thumb-img { width: 100px; height: 75px; border-radius: 6px; border: 1px solid #e5e7eb; cursor: pointer; transition: transform 0.2s;
    &:hover { transform: scale(1.05); } }
}
</style>
